// Timestamps in the viewer's own timezone.
//
// The server renders in UTC — `config.time_zone` is unset, so that is Rails'
// default — and a client in São Paulo reading "21:54" for something that
// happened at 18:54 their time has been shown a wrong number, not a converted
// one. It reads as wrong in the most obvious way possible: an event timestamped
// in the future.
//
// PER-VIEWER, NOT PER-ACCOUNT. A timezone stored on the customer would have to
// be set, kept current, and would still be wrong the week they travel — which
// is how this was noticed. The browser already knows where it is, so it does
// the conversion and nobody configures anything.
//
// Mounted on <body>, so it converts every `<time datetime="…">` on any page
// without each view opting in. That element is exactly the right subset: the
// datetime attribute is a machine-readable instant with its offset, so the
// conversion is exact, and anything not marked up as a time is left alone.
//
// The server-rendered text inside stays as the fallback — correct-ish and
// readable with JavaScript off, replaced the moment this runs.
//
//   <time datetime="2026-09-02T21:54:00Z">02/09/2026 · 21:54</time>
//   → "02/09/2026 · 18:54" for a viewer at UTC-3, titled with the zone.
//
// data-local-time-style-value picks the shape: "datetime" (default), "date",
// "time". A single element can override with data-local-time-style.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { style: { type: String, default: "datetime" } }

  connect() {
    this.render()
    // Anything arriving after load — a Turbo frame, a modal — gets converted
    // too. Cheap: the callback only walks what actually changed.
    this.observer = new MutationObserver((records) => this.onMutations(records))
    this.observer.observe(this.element, { childList: true, subtree: true })
  }

  disconnect() {
    this.observer?.disconnect()
  }

  onMutations(records) {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue
        if (node.matches?.("time[datetime]")) this.convert(node)
        node.querySelectorAll?.("time[datetime]").forEach((el) => this.convert(el))
      }
    }
  }

  render() {
    this.element.querySelectorAll("time[datetime]").forEach((el) => this.convert(el))
  }

  convert(el) {
    // Idempotent: the observer can see the same element twice, and re-running
    // must not reformat an already-local string as if it were UTC.
    if (el.dataset.localTimeDone === "1") return

    const raw = el.getAttribute("datetime")
    if (!raw) return
    const date = new Date(raw)
    if (isNaN(date.getTime())) return

    const style = el.dataset.localTime || this.styleValue
    const locale = document.documentElement.lang || navigator.language || undefined

    try {
      el.textContent = this.formatFor(date, style, locale)
      el.title = new Intl.DateTimeFormat(locale, {
        dateStyle: "full", timeStyle: "long"
      }).format(date)
      el.dataset.localTimeDone = "1"
    } catch (e) {
      // A bad locale or an unsupported option must not blank a timestamp — the
      // server's text is still sitting there and is better than nothing.
    }
  }

  formatFor(date, style, locale) {
    const d = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" })
    const t = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" })

    if (style === "date") return d.format(date)
    if (style === "time") return t.format(date)
    // Matches the server's "<date> · <time>" shape, so the panel does not
    // reflow when this replaces it.
    return `${d.format(date)} · ${t.format(date)}`
  }
}
